// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import withDynamicGoogleMaps from "@insite/client-framework/Common/withDynamicGoogleMaps";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadZipCode from "@west/App/Handlers/ZipCode/LoadZipCode";
import updateZipCode from "@west/App/Handlers/ZipCode/UpdateZipCode";
import { CustomAppState } from "@west/App/Store/Reducers";
import EditZipCode from "@west/App/Widgets/Header/EditZipCode";
import { EMPTY_VALUE, XNGAGE_CUSTOM_WIDGET } from "@west/XDL/constants";
import React, { FC, useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";

interface OwnProps {
    useLoadScript: ({ googleMapsApiKey }: { googleMapsApiKey: any }) => {
        isLoaded: boolean;
        loadError: Error | undefined;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    settings: getSettingsCollection(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

interface ZipCodeStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    valueContainer?: InjectableCss;
    value?: TypographyPresentationProps;
    editIconClickable?: ClickablePresentationProps;
    editIcon?: IconPresentationProps;
    modal?: ModalPresentationProps;
}

export const zipCodeStyles: ZipCodeStyles = {};

const styles = zipCodeStyles;
const ZipCode: FC<Props> = ({ settings, useLoadScript }) => {
    const { googleMapsApiKey } = settings.websiteSettings;
    const { isLoaded, loadError } = useLoadScript({ googleMapsApiKey });

    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const [error, setError] = useState("");
    const zipCode = useAppSelector(state => (state as CustomAppState).xHeader.zipCodeAddress?.zipCode);

    useEffect(() => {
        if (isLoaded || loadError) {
            dispatch(loadZipCode({}));
        }
    }, [isLoaded, loadError]);

    const onUpdateHandler = (value?: string) => {
        if (!value) {
            return;
        }

        dispatch(
            updateZipCode({
                postalCode: value,
                onSuccess: _ => {
                    setIsOpen(false);
                    setError("");
                },
                onError: error => {
                    setError(error);
                },
            }),
        );
    };

    const onChangeHandler = () => {
        setError("");
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="zip-code-widget">
            <Typography {...styles.title}>{translate("ZIP Code")}:</Typography>
            <StyledWrapper {...styles.valueContainer}>
                <Typography {...styles.value}>{zipCode || EMPTY_VALUE}</Typography>
                <Clickable
                    onClick={() => setIsOpen(true)}
                    {...styles.editIconClickable}
                    aria-label={translate("Edit Zip Code")}
                >
                    <Icon {...styles.editIcon} />
                </Clickable>
            </StyledWrapper>
            <Modal
                isOpen={isOpen}
                handleClose={() => {
                    setIsOpen(false);
                    setError("");
                }}
                headline={translate("Update ZIP Code")}
                {...styles.modal}
            >
                <EditZipCode
                    currentZipCode={zipCode}
                    onUpdateHandler={onUpdateHandler}
                    error={error}
                    onChangeHandler={onChangeHandler}
                />
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withDynamicGoogleMaps(ZipCode)),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
    },
};

export default widgetModule;

export { ZipCode };
